/// <amd-module name="VSS/Features/Markdown/MarkdownRendererComponent" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { shallowCompare } from "VSS/Core/Util/Object";
import { endsWith, format } from "VSS/Core/Util/String";
import { MarkdownRenderer } from "VSS/Features/Markdown/MarkdownRenderer";
import { IMarkdownRendererComponentProps } from "VSS/Features/Markdown/MarkdownRendererComponent.Props";
import { IMarkdownTransformContext, MarkdownRendererOptions } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { openImageInNewTab } from "VSSUI/Utilities/Image";
import { htmlEncode } from "VSS/Features/Markdown/Utils/UrlHelper";
import { IMarkdownHighlightService } from "VSS/Features/MarkdownHighlight/IMarkdownHighlightService";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService, IVssScenario } from "VSS/Platform/Performance";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";

const enum ServiceLoadingStatus {
    NotStarted,
    InProgress,
    Completed,
    Failed
}
const MdHighlightServiceContributionId = "ms.vss-features.markdown-highlight-service";

const transformReplacementPrefix = "md-transform-replacement-";
let transformReplacementId = 0;

export interface IMarkdownRendererComponentState {
    renderedHtml: string;
}

export class MarkdownRendererComponent extends VssComponent<IMarkdownRendererComponentProps, IMarkdownRendererComponentState> {
    private _markdownContainer = new ObservableValue<HTMLDivElement | null>(null);
    private _perfScenario: IVssScenario | undefined;
    private _renderer: MarkdownRenderer;
    private _transformReplacements = new ObservableArray<React.ReactNode>();
    private _mathPluginEnabled: boolean;
    private _katexLoadingStatus: ServiceLoadingStatus;
    private _mdHighlighterLoadingStatus: ServiceLoadingStatus;

    constructor(props: IMarkdownRendererComponentProps, context: IVssComponentContext) {
        super(props, context);

        this._katexLoadingStatus = ServiceLoadingStatus.NotStarted;
        this._mdHighlighterLoadingStatus = ServiceLoadingStatus.NotStarted;

        const renderOptions: MarkdownRendererOptions | undefined = this._getRenderOptionsWithKatexOptionsIfNeeded(props.options, props.rawContent);
        this._renderer = this._createMarkdownRenderer(renderOptions);

        if (props.perfScenarioName) {
            const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            this._perfScenario = perfService.startScenario(props.perfScenarioName, false);
        }

        this.state = {
            renderedHtml: this._executeRenderer(props.rawContent)
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        _attachLinkClickHandler(this._markdownContainer.value, this.props.linkClickHandler);
        if (this.props.onInitialRender) {
            this.props.onInitialRender();
        }

        if (this.props.customSyntaxProcessor) {
            this._renderHtml(this.props.rawContent);
        } else {
            this._endPerformanceScenario();
        }
    }

    public componentDidUpdate(previousProps: IMarkdownRendererComponentProps, previousState: IMarkdownRendererComponentState): void {
        super.componentDidUpdate(previousProps, previousState);

        if (
            !shallowCompare(this.props.options || {}, previousProps.options || {}) ||
            (!this._mathPluginEnabled && previousProps.rawContent !== this.props.rawContent && this._hasContentMathBlock(this.props.rawContent))
        ) {
            const renderOptions: MarkdownRendererOptions | undefined = this._getRenderOptionsWithKatexOptionsIfNeeded(
                this.props.options,
                this.props.rawContent
            );
            this._renderer = this._createMarkdownRenderer(renderOptions);
            this._renderHtml(this.props.rawContent);
            return;
        }

        if (previousProps.rawContent !== this.props.rawContent || previousProps.previewMode !== this.props.previewMode) {
            this._renderHtml(this.props.rawContent);
        }

        _attachLinkClickHandler(this._markdownContainer.value, this.props.linkClickHandler);
        if (this.props.injectExtensionMarkup) {
            this.props.injectExtensionMarkup();
        }
    }

    public render(): JSX.Element {
        return (
            <>
                <Observer transformReplacements={this._transformReplacements}>
                    {(props: { transformReplacements: React.ReactNode[] }) => props.transformReplacements}
                </Observer>
                {
                    /* tslint:disable:react-no-dangerous-html */
                    <div
                        onDoubleClick={this.props.options?.clickableImages ? openImageInNewTab : undefined}
                        className={css("markdown-content", this.props.className)}
                        dangerouslySetInnerHTML={{ __html: this.state.renderedHtml }}
                        ref={ref => (this._markdownContainer.value = ref)}
                        aria-label={this.props["aria-label"]}
                    />
                    /* tslint:enable:react-no-dangerous-html */
                }
            </>
        );
    }

    public getRenderedContent(): string | undefined {
        return this._markdownContainer.value ? this._markdownContainer.value.innerHTML : undefined;
    }

    // Public for UTs
    public _highlight = (code: string, language: string): string | undefined => {
        if (!language) {
            return _fallbackHtmlForCodeBlock(code);
        }

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const markdownHighlightService = contributionService.getServiceSync<IMarkdownHighlightService>(MdHighlightServiceContributionId);
        if (markdownHighlightService) {
            return _tryHighlight(markdownHighlightService, code, language);
        } else {
            this._mdHighlighterLoadingStatus = ServiceLoadingStatus.InProgress;
            this.trackPromise(contributionService.getService<IMarkdownHighlightService>(MdHighlightServiceContributionId))
                .promise.then(
                    (markdownHighlightService: IMarkdownHighlightService | undefined) => {
                        this._mdHighlighterLoadingStatus = markdownHighlightService ? ServiceLoadingStatus.Completed : ServiceLoadingStatus.Failed;
                    },
                    () => {
                        this._mdHighlighterLoadingStatus = ServiceLoadingStatus.Failed;
                    }
                )
                .then(() => {
                    this._renderHtml(this.props.rawContent);
                });
        }

        return undefined;
    };

    private _createMarkdownRenderer(overriddenOptions?: MarkdownRendererOptions): MarkdownRenderer {
        const options = _getMarkdownRendererOptions(this._highlight, overriddenOptions);

        return new MarkdownRenderer(this.context.pageContext, options);
    }

    private _renderHtml(rawContent: string): void {
        if (this._katexLoadingStatus != ServiceLoadingStatus.InProgress && this._mdHighlighterLoadingStatus != ServiceLoadingStatus.InProgress) {
            if (this.props.customSyntaxProcessor) {
                this.trackPromise(this.props.customSyntaxProcessor.preProcess(rawContent)).promise.then((value: string) => {
                    this._postProcessRenderedHtml(value);
                });
            } else {
                this._postProcessRenderedHtml(rawContent);
            }
        }
    }

    private _postProcessRenderedHtml(markup: string): void {
        const renderedHtml = this._executeRenderer(markup);

        if (this.props.customSyntaxProcessor) {
            this.trackPromise(this.props.customSyntaxProcessor.postProcess(renderedHtml)).promise.then((value: string) => {
                this._endPerformanceScenario(true);
                this.setState({ renderedHtml: value });
            });
        } else {
            this._endPerformanceScenario();
            if (this.state.renderedHtml !== renderedHtml) {
                this.setState({ renderedHtml: renderedHtml });
            }
        }
    }

    private _executeRenderer(markup: string): string {
        const { options } = this.props;

        // Run transformers on pre-rendered markup
        const transformReplacements: React.ReactNode[] = [];
        let transformContext: IMarkdownTransformContext;

        if (options && options.transformers && options.transformers.length) {
            const registerReplacement = (selector: string, replacement: React.ReactNode): void => {
                transformReplacements.push(
                    <Observer container={this._markdownContainer} key={selector}>
                        {(props: { container: HTMLElement | null }) => {
                            const hostElement = props.container && props.container.querySelector(selector);
                            return hostElement ? ReactDOM.createPortal(replacement, hostElement) : null;
                        }}
                    </Observer>
                );
            };

            transformContext = {
                markup,
                originalMarkup: markup,
                createReplacement: (
                    replacement: React.ReactNode,
                    containerElementType: "div" | "span" = "div",
                    containerClassName?: string
                ): string => {
                    const replacementId = transformReplacementPrefix + transformReplacementId++;
                    registerReplacement("#" + replacementId, replacement);
                    const classNameAttr = containerClassName ? ` class="${containerClassName}"` : "";
                    return `<${containerElementType} id="${replacementId}"${classNameAttr}></${containerElementType}>`;
                },
                registerReplacement
            };

            for (const transformer of options.transformers) {
                transformer(transformContext);
            }

            markup = transformContext.markup;
        }

        if (transformReplacements.length > 0 || this._transformReplacements.length > 0) {
            this._transformReplacements.value = transformReplacements;
        }

        const renderedHtml = this._renderer.renderHtml(markup);
        return renderedHtml;
    }

    private _endPerformanceScenario = (isCustomSyntaxProcessed?: boolean): void => {
        if (this._perfScenario) {
            this._perfScenario.addProperties({ isCustomSyntaxProcessed });
            this._perfScenario.featureArea = "MarkdownRendering";
            this._perfScenario.complete();
            this._perfScenario = undefined;
        }
    };

    private _getRenderOptionsWithKatexOptionsIfNeeded(
        options: MarkdownRendererOptions | undefined,
        content: string
    ): MarkdownRendererOptions | undefined {
        let renderOptions: MarkdownRendererOptions | undefined = options;
        if (this._hasContentMathBlock(content)) {
            const katexRenderOptions: MarkdownRendererOptions = {
                enableKatex: true,
                onKatexLoadStarted: () => {
                    this._katexLoadingStatus = ServiceLoadingStatus.InProgress;
                },
                onKatexLoad: () => {
                    this._katexLoadingStatus = ServiceLoadingStatus.Completed;
                    this._renderHtml(this.props.rawContent);
                },
                onKatexLoadFailed: () => {
                    this._katexLoadingStatus = ServiceLoadingStatus.Failed;
                    this._renderHtml(this.props.rawContent);
                }
            };
            renderOptions = { ...katexRenderOptions, ...renderOptions };
        }

        return renderOptions;
    }

    private _hasContentMathBlock(markup: string): boolean {
        if (!this._mathPluginEnabled) {
            this._mathPluginEnabled = _hasContentMathBlock(markup);
        }

        return this._mathPluginEnabled;
    }
}

VssComponent.register("ms.vss-features.markdown-component", MarkdownRendererComponent);

// exported for UT
export function _getMarkdownRendererOptions(
    highlight: (code: string, language: string) => string | undefined,
    overriddenOptions?: MarkdownRendererOptions
) {
    const highlightOption = overriddenOptions && overriddenOptions.highlight;

    const options: MarkdownRendererOptions = {
        enableEmoji: true,
        highlight: highlightOption
            ? (code: string, language: string): string | undefined => {
                  const value = highlightOption(code, language) as any;
                  return value !== undefined ? value : highlight(code, language);
              }
            : highlight,
        enableImageSize: true,
        linkify: true,
        linkifyTlds: ["biz", "com", "edu", "gov", "net", "org", "pro", "web", "aero", "asia", "coop", "info", "museum", "name", "shop", "рф", "io"],
        typographer: false,
        validateLink: _validateLinkProtocol,
        ...overriddenOptions
    };

    return options;
}

// exported for UT
export function _attachLinkClickHandler(container: HTMLDivElement | null, linkClickHandler?: (event: MouseEvent) => boolean): void {
    if (!container) {
        return;
    }

    const linksCollection = container.getElementsByTagName("a");
    for (let i = 0; i < linksCollection.length; i++) {
        const link = linksCollection[i];
        // Mermaid return non-string href (SVGAnimatedString) for links in htmlLabels
        if (typeof link.href == "string") {
            if (endsWith(link.href, "Invalid uri value")) {
                // Block invalid uri filtered by html sanitizer
                link.href = "#";
            } else {
                if (linkClickHandler) {
                    link.onclick = linkClickHandler as any;
                }
            }
        }
    }
}

const KATEX_BLOCK_RE = /\$[^\$]+\$/;
// exported for UT
export function _hasContentMathBlock(markup: string): boolean {
    return KATEX_BLOCK_RE.test(markup);
}

const BAD_PROTOCOLS = /^(vbscript|javascript|file|data):/;

/**
 * Inspect the provided urls for all protocols we want to prevent
 * The markdown-it defaults are to allow the data protocol if it is an image but
 * we don't want to allow embeded images that we'll have to scan for malicious content
 */
// exported for UT
export function _validateLinkProtocol(url: string): boolean {
    const str = url.trim().toLowerCase();
    return !BAD_PROTOCOLS.test(str);
}

const htmlFormatStringForCodeBlock = `<pre class="hljs"><code{0}>{1}</code></pre>`;
//  exported for UT
export function _tryHighlight(markdownHighlightService: IMarkdownHighlightService, code: string, language: string): string {
    try {
        return format(htmlFormatStringForCodeBlock, ` class="${language}"`, markdownHighlightService.highlight(code, language));
    } catch (e) {
        return _fallbackHtmlForCodeBlock(code);
    }
}

function _fallbackHtmlForCodeBlock(code: string): string {
    return format(htmlFormatStringForCodeBlock, "", htmlEncode(code));
}
